import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import {
    Loader2,
    Download,
    Mail,
    ArrowRight,
    CheckCircle2,
    ExternalLink,
    Copy,
    Check,
    Sparkles,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import useMemberSession from "@/hooks/useMemberSession";
import { AWAKE_URLS } from "@/lib/memberSession";
import { WelcomeBanner } from "@/components/report/WelcomeBanner";
import { AcademyPlanDialog } from "@/components/report/AcademyPlanDialog";
import { AcademyInterestDialog } from "@/components/report/AcademyInterestDialog";

function Pill({ children, color = "teal" }) {
    const map = {
        teal: "border-weave-teal text-weave-teal",
        red: "border-weave-red text-weave-red",
        yellow: "border-weave-yellow text-[#a17320]",
        ink: "border-weave-ink text-weave-ink",
    };
    return (
        <span
            className={`inline-flex items-center gap-2 border px-3 py-1 text-xs uppercase tracking-editorial font-medium ${map[color]}`}
        >
            {children}
        </span>
    );
}

function MarketBlock({ data, accent = "teal" }) {
    if (!data) return null;
    const accentMap = {
        teal: "border-l-weave-teal",
        red: "border-l-weave-red",
        yellow: "border-l-weave-yellow",
    };
    return (
        <div
            className={`bg-weave-surface border border-weave-border border-l-4 ${accentMap[accent]} p-8`}
            data-testid={`market-${data.label?.toLowerCase().split(" ")[0]}`}
        >
            <p className="editorial-label text-weave-slate mb-3">
                {data.label}
            </p>
            <p className="text-4xl md:text-5xl font-light text-weave-ink tracking-tight">
                {data.value}
            </p>
            <p className="text-xs text-weave-muted mt-1 uppercase tracking-editorial">
                {data.unit}
            </p>
            <div className="hairline my-5" />
            <p className="text-sm text-weave-slate leading-relaxed">
                {data.narrative}
            </p>
            {data.sources?.length > 0 && (
                <div className="mt-5 space-y-1.5">
                    <p className="editorial-label text-weave-muted">Sources</p>
                    {data.sources.map((s, i) => (
                        <a
                            key={i}
                            href={s.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-weave-teal hover:underline flex items-start gap-1.5"
                            data-testid={`source-link-${i}`}
                        >
                            <ExternalLink className="w-3 h-3 mt-0.5 flex-shrink-0" />
                            <span>{s.title}</span>
                        </a>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function Report() {
    const { id } = useParams();
    const { isMember, claims } = useMemberSession();
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [emailDialogOpen, setEmailDialogOpen] = useState(false);
    const [email, setEmail] = useState("");
    const [isClubMember, setIsClubMember] = useState(false);
    const [unlocking, setUnlocking] = useState(false);
    const [unlocked, setUnlocked] = useState(false);
    const [trial, setTrial] = useState(null);
    const [claimDialogOpen, setClaimDialogOpen] = useState(false);
    const [claimEmail, setClaimEmail] = useState("");
    const [claiming, setClaiming] = useState(false);
    const [copiedCode, setCopiedCode] = useState(false);
    const [academyDialogOpen, setAcademyDialogOpen] = useState(false);
    const [academyEmail, setAcademyEmail] = useState("");
    const [academyNote, setAcademyNote] = useState("");
    const [academyType, setAcademyType] = useState("full_academy");
    const [academyRegistering, setAcademyRegistering] = useState(false);
    const [academyRegistered, setAcademyRegistered] = useState(false);
    // Academy plan-picker (Stripe checkout)
    const [planDialogOpen, setPlanDialogOpen] = useState(false);
    const [academyPlans, setAcademyPlans] = useState([]);
    const [checkoutLoading, setCheckoutLoading] = useState(null); // plan_id being processed
    const [searchParams, setSearchParams] = useSearchParams();
    const academyWelcome = searchParams.get("academy"); // "welcome" | "cancelled" | null
    const selectedPlanId = searchParams.get("plan");
    // Warm Academy lead — checkbox on the career-trial-code card
    const [waitlistedForAcademy, setWaitlistedForAcademy] = useState(false);
    const [waitlistingForAcademy, setWaitlistingForAcademy] = useState(false);

    // Pre-fill email and club-member flag if the visitor arrived as a paid member.
    useEffect(() => {
        if (claims?.e) {
            setEmail((v) => v || claims.e);
            setClaimEmail((v) => v || claims.e);
        }
        if (isMember) setIsClubMember(true);
    }, [isMember, claims]);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/reports/${id}`);
                setReport(data);
                if (data.email_captured) setUnlocked(true);
            } catch (err) {
                toast.error("Report not found.");
            } finally {
                setLoading(false);
            }
        })();
    }, [id]);

    const handleUnlock = async (e) => {
        e.preventDefault();
        if (!email || !email.includes("@")) {
            toast.error("Please enter a valid email.");
            return;
        }
        setUnlocking(true);
        try {
            const { data } = await api.post("/leads/capture", {
                email,
                report_id: id,
                is_club_member: isClubMember,
                send_email: true,
            });
            setUnlocked(true);
            setEmailDialogOpen(false);
            if (data.email_sent) {
                toast.success("Sent to your inbox. Downloading PDF too…");
            } else {
                toast.success("Downloading your PDF now…");
            }
            // trigger PDF download
            setTimeout(() => downloadPdf(), 400);
        } catch (err) {
            toast.error(
                err?.response?.data?.detail ||
                    "Couldn't send. Please try again."
            );
        } finally {
            setUnlocking(false);
        }
    };

    const downloadPdf = () => {
        window.open(`${API}/reports/${id}/pdf`, "_blank");
    };

    const handleDownloadClick = () => {
        // Members are already known to us — go straight to PDF, no email gate.
        if (unlocked || isMember) {
            downloadPdf();
        } else {
            setEmailDialogOpen(true);
        }
    };

    const claimTrial = async (e) => {
        e?.preventDefault();
        const emailToUse =
            (claimEmail || report?.email_captured || "").trim().toLowerCase();
        if (!emailToUse || !emailToUse.includes("@")) {
            toast.error("Enter a valid email to claim your free month.");
            return;
        }
        setClaiming(true);
        try {
            const { data } = await api.post("/trials/claim", {
                email: emailToUse,
                report_id: id,
            });
            setTrial(data);
            setClaimDialogOpen(false);
            toast.success("Your free month code is ready. Check your inbox too.");
        } catch (err) {
            toast.error(
                err?.response?.data?.detail ||
                    "Couldn't mint your code — please try again."
            );
        } finally {
            setClaiming(false);
        }
    };

    const copyCode = async () => {
        if (!trial?.code) return;
        try {
            await navigator.clipboard.writeText(trial.code);
            setCopiedCode(true);
            toast.success("Code copied.");
            setTimeout(() => setCopiedCode(false), 2500);
        } catch {
            toast.error("Couldn't copy — please select and copy manually.");
        }
    };

    // After email is captured, check if a trial already exists for that email
    useEffect(() => {
        const captured = report?.email_captured;
        if (!captured) return;
        api
            .get(`/trials/by-email/${encodeURIComponent(captured)}`)
            .then((r) => setTrial(r.data))
            .catch(() => {});
    }, [report?.email_captured]);

    const trialExpiresHuman = trial?.expires_at
        ? new Date(trial.expires_at * 1000).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
          })
        : null;

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-weave-bg">
                <Loader2 className="w-8 h-8 animate-spin text-weave-teal" />
            </div>
        );
    }

    if (!report) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-weave-bg">
                <div className="text-center" data-testid="report-not-found">
                    <p className="editorial-label text-weave-slate">
                        404 — Report not found
                    </p>
                    <Link to="/" className="btn-primary mt-6">
                        Back to home
                    </Link>
                </div>
            </div>
        );
    }

    const c = report.content || {};
    const market = c.market || {};
    const isCareer = report.flow_type === "career";
    const pc = c.personal_clarity || null;
    const bc = c.business_clarity || null;
    const ec = c.external_clarity || null;

    const registerAcademyInterest = async (e) => {
        e?.preventDefault();
        const emailToUse = (academyEmail || report?.email_captured || claims?.e || "").trim().toLowerCase();
        if (!emailToUse || !emailToUse.includes("@")) {
            toast.error("Enter a valid email so we can hold your place.");
            return;
        }
        setAcademyRegistering(true);
        try {
            await api.post("/academy/interest", {
                email: emailToUse,
                report_id: id,
                first_name: (report?.business_name || "").split(" ")[0] || null,
                interest_type: academyType,
                note: academyNote || null,
            });
            setAcademyRegistered(true);
            setAcademyDialogOpen(false);
            toast.success("Interest registered. We'll be in touch.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't register — please try again.");
        } finally {
            setAcademyRegistering(false);
        }
    };

    const openPlanPicker = async () => {
        setPlanDialogOpen(true);
        if (academyPlans.length === 0) {
            try {
                const { data } = await api.get("/academy/plans");
                setAcademyPlans(data.plans || []);
            } catch {
                setAcademyPlans([]);
            }
        }
    };

    const startAcademyCheckout = async (planId) => {
        const emailToUse = (report?.email_captured || claims?.e || academyEmail || "").trim().toLowerCase();
        if (!emailToUse || !emailToUse.includes("@")) {
            setPlanDialogOpen(false);
            setAcademyDialogOpen(true);
            toast.info("Just need your email so we can send the receipt.");
            return;
        }
        setCheckoutLoading(planId);
        try {
            const { data } = await api.post("/academy/checkout", {
                plan_id: planId,
                email: emailToUse,
                report_id: id,
            });
            if (data?.url) {
                window.location.assign(data.url);
                return;
            }
            toast.error("Couldn't start checkout — please try again.");
        } catch (err) {
            const status = err?.response?.status;
            if (status === 501) {
                // Plan not configured yet — fall back to external Founder-Cap link
                window.open("https://founder-cap.wearetheweave.co.uk", "_blank", "noopener,noreferrer");
                toast.info("This plan isn't wired to Stripe yet — opening founder-cap for now.");
            } else {
                toast.error(err?.response?.data?.detail?.message || "Checkout failed. Please try again.");
            }
        } finally {
            setCheckoutLoading(null);
        }
    };

    const dismissWelcome = () => {
        const newParams = new URLSearchParams(searchParams);
        newParams.delete("academy");
        newParams.delete("plan");
        setSearchParams(newParams, { replace: true });
    };

    const addToAcademyWaitlist = async (e) => {
        // Checkbox onChange — checking = register interest; unchecking = ignored
        if (!e.target.checked || waitlistedForAcademy) return;
        const emailToUse = (claimEmail || report?.email_captured || claims?.e || "").trim().toLowerCase();
        if (!emailToUse || !emailToUse.includes("@")) return;
        setWaitlistingForAcademy(true);
        try {
            await api.post("/academy/interest", {
                email: emailToUse,
                report_id: id,
                first_name: (report?.business_name || "").split(" ")[0] || null,
                interest_type: "waitlist_from_trial",
                note: "Opted in from the 1% Club trial-code card on the career report.",
            });
            setWaitlistedForAcademy(true);
            toast.success("You're on the list — we'll be in touch when the next cohort opens.");
        } catch (err) {
            // Uncheck the box on failure so it doesn't look silently accepted
            e.target.checked = false;
            toast.error(err?.response?.data?.detail || "Couldn't add you — please try again.");
        } finally {
            setWaitlistingForAcademy(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col bg-weave-bg">
            <Header />

            {/* WELCOME / CHECKOUT-RETURN BANNER */}
            <WelcomeBanner
                state={academyWelcome}
                planId={selectedPlanId}
                onDismiss={dismissWelcome}
            />

            {/* COVER */}
            <section className="border-b border-weave-ink" data-testid="report-cover">
                <div className="max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-24">
                    <Pill color="teal">Opportunity Report</Pill>
                    <p
                        className="editorial-label text-weave-slate mt-6"
                        data-testid="report-business-name"
                    >
                        Prepared for {report.business_name}
                    </p>
                    <h1
                        className="text-4xl md:text-6xl font-light tracking-tightest text-weave-ink mt-4 leading-[1.05]"
                        data-testid="report-headline"
                    >
                        {c.headline}
                    </h1>
                    <div className="hairline-ink mt-8" />
                    <p
                        className="text-xl md:text-2xl text-weave-ink mt-8 leading-relaxed max-w-3xl font-light"
                        data-testid="report-summary"
                    >
                        {c.opportunity_summary}
                    </p>
                    {c.leverage_insight && (
                        <div
                            className="mt-10 bg-weave-surface-alt border-l-4 border-weave-red p-6 md:p-8"
                            data-testid="leverage-callout"
                        >
                            <p className="editorial-label text-weave-red mb-3">
                                Where your leverage lives
                            </p>
                            <p className="text-lg md:text-xl text-weave-ink leading-snug">
                                {c.leverage_insight}
                            </p>
                        </div>
                    )}

                    {c.mission_alignment && (c.mission_alignment.stated_direction || c.mission_alignment.narrative) && (
                        <div
                            className="mt-6"
                            data-testid="mission-alignment-block"
                        >
                            <div className="flex items-center gap-2 mb-3">
                                <span
                                    className={`w-2 h-2 rounded-full ${
                                        c.mission_alignment.on_compass
                                            ? "bg-weave-teal"
                                            : "bg-weave-red"
                                    }`}
                                    aria-hidden
                                />
                                <p
                                    className={`editorial-label ${
                                        c.mission_alignment.on_compass
                                            ? "text-weave-teal"
                                            : "text-weave-red"
                                    }`}
                                    data-testid="mission-compass-label"
                                >
                                    {c.mission_alignment.on_compass
                                        ? "On compass · honours your direction"
                                        : "Off compass · trade-off ahead"}
                                </p>
                            </div>
                            {c.mission_alignment.stated_direction && (
                                <p className="text-xl md:text-2xl text-weave-ink leading-snug font-light">
                                    "{c.mission_alignment.stated_direction}"
                                </p>
                            )}
                            {c.mission_alignment.narrative && (
                                <p className="mt-4 text-weave-slate leading-relaxed max-w-3xl">
                                    {c.mission_alignment.narrative}
                                </p>
                            )}
                            {!c.mission_alignment.on_compass &&
                                c.mission_alignment.warning && (
                                    <div
                                        className="mt-5 bg-[#fdecea] border-l-4 border-weave-red p-5"
                                        data-testid="mission-warning"
                                    >
                                        <p className="editorial-label text-weave-red mb-2">
                                            Worth pausing
                                        </p>
                                        <p className="text-weave-ink leading-relaxed">
                                            {c.mission_alignment.warning}
                                        </p>
                                    </div>
                                )}
                        </div>
                    )}

                    <div className="mt-10 flex flex-wrap items-center gap-4">
                        <button
                            onClick={handleDownloadClick}
                            className="btn-primary"
                            data-testid="report-download-btn"
                        >
                            <Download className="w-4 h-4" />
                            {unlocked || isMember
                                ? "Download PDF"
                                : "Download PDF (free)"}
                        </button>
                        {isMember ? (
                            <a
                                href={AWAKE_URLS.dashboard}
                                className="btn-secondary"
                                data-testid="report-dashboard-cta"
                            >
                                Back to your dashboard
                                <ArrowRight className="w-4 h-4" />
                            </a>
                        ) : (
                            <a
                                href="https://awake.wearetheweave.co.uk/join"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn-secondary"
                                data-testid="report-club-cta"
                            >
                                Join the 1% Club
                                <ArrowRight className="w-4 h-4" />
                            </a>
                        )}
                    </div>
                </div>
            </section>

            <main className="max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-24 space-y-20">
                {/* PERSONA + SEGMENT */}
                <section data-testid="persona-section">
                    <div className="grid md:grid-cols-12 gap-10">
                        <div className="md:col-span-4">
                            <p className="editorial-label text-weave-teal">
                                01 — Persona
                            </p>
                            <h2 className="text-3xl md:text-4xl font-light tracking-tight text-weave-ink mt-3">
                                Who this is for
                            </h2>
                        </div>
                        <div className="md:col-span-8 space-y-6">
                            {c.persona && (
                                <div className="border border-weave-ink p-8">
                                    <div className="flex items-baseline justify-between gap-4 flex-wrap">
                                        <div>
                                            <p
                                                className="text-2xl text-weave-ink font-medium"
                                                data-testid="persona-name"
                                            >
                                                {c.persona.name}
                                            </p>
                                            <p className="text-weave-slate mt-1">
                                                {c.persona.role}
                                            </p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-xs text-weave-muted uppercase tracking-editorial">
                                                Age {c.persona.age_range}
                                            </p>
                                            <p className="text-xs text-weave-slate mt-1">
                                                {c.persona.archetype}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="hairline my-6" />
                                    <p className="text-weave-slate leading-relaxed">
                                        {c.persona.bio}
                                    </p>
                                    <div className="grid md:grid-cols-2 gap-6 mt-8">
                                        <div>
                                            <p className="editorial-label text-weave-teal mb-3">
                                                Motivations
                                            </p>
                                            <ul className="space-y-2">
                                                {(c.persona.motivations || []).map(
                                                    (m, i) => (
                                                        <li
                                                            key={i}
                                                            className="text-weave-slate text-sm flex gap-2"
                                                        >
                                                            <span className="text-weave-teal">·</span>
                                                            <span>{m}</span>
                                                        </li>
                                                    )
                                                )}
                                            </ul>
                                        </div>
                                        <div>
                                            <p className="editorial-label text-weave-red mb-3">
                                                Frustrations
                                            </p>
                                            <ul className="space-y-2">
                                                {(c.persona.frustrations || []).map(
                                                    (m, i) => (
                                                        <li
                                                            key={i}
                                                            className="text-weave-slate text-sm flex gap-2"
                                                        >
                                                            <span className="text-weave-red">·</span>
                                                            <span>{m}</span>
                                                        </li>
                                                    )
                                                )}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {c.segment && (
                                <div
                                    className="pt-6 border-t border-weave-border"
                                    data-testid="segment-block"
                                >
                                    <p className="editorial-label text-weave-slate">
                                        The segment
                                    </p>
                                    <h3 className="text-2xl text-weave-ink mt-2 font-medium">
                                        {c.segment.name}
                                    </h3>
                                    <p className="text-weave-slate mt-2 leading-relaxed">
                                        {c.segment.description}
                                    </p>
                                    {c.segment.where_they_are && (
                                        <p className="text-sm text-weave-muted mt-3">
                                            <span className="editorial-label">
                                                Where:
                                            </span>{" "}
                                            <span className="text-weave-slate">
                                                {c.segment.where_they_are}
                                            </span>
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </section>

                {/* JTBD */}
                {c.jtbd && (
                    <section data-testid="jtbd-section">
                        <div className="grid md:grid-cols-12 gap-10">
                            <div className="md:col-span-4">
                                <p className="editorial-label text-weave-teal">
                                    02 — Job to be done
                                </p>
                                <h2 className="text-3xl md:text-4xl font-light tracking-tight text-weave-ink mt-3">
                                    What they're hiring you for
                                </h2>
                            </div>
                            <div className="md:col-span-8">
                                <div className="bg-weave-surface-alt border-l-4 border-weave-teal p-8">
                                    <p
                                        className="text-xl md:text-2xl text-weave-ink leading-relaxed font-light"
                                        data-testid="jtbd-statement"
                                    >
                                        "{c.jtbd.job}"
                                    </p>
                                </div>
                                <div className="grid md:grid-cols-2 gap-px bg-weave-border mt-px">
                                    <div className="bg-weave-surface p-6">
                                        <p className="editorial-label text-weave-red mb-3">
                                            Pain removed
                                        </p>
                                        <p className="text-weave-slate leading-relaxed">
                                            {c.jtbd.pain_removed}
                                        </p>
                                    </div>
                                    <div className="bg-weave-surface p-6">
                                        <p className="editorial-label text-[#a17320] mb-3">
                                            Gain created
                                        </p>
                                        <p className="text-weave-slate leading-relaxed">
                                            {c.jtbd.gain_created}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                )}

                {/* MARKET */}
                <section data-testid="market-section">
                    <div className="grid md:grid-cols-12 gap-10 mb-10">
                        <div className="md:col-span-4">
                            <p className="editorial-label text-weave-teal">
                                03 — Market size
                            </p>
                            <h2 className="text-3xl md:text-4xl font-light tracking-tight text-weave-ink mt-3">
                                Built bottom-up
                            </h2>
                        </div>
                        <div className="md:col-span-8 self-end">
                            <p className="text-weave-slate leading-relaxed">
                                Each figure below is built from accredited
                                public sources. Click any source to verify it
                                yourself.
                            </p>
                        </div>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6">
                        <MarketBlock data={market.tam} accent="teal" />
                        <MarketBlock data={market.sam} accent="yellow" />
                        <MarketBlock data={market.som} accent="red" />
                    </div>

                    {c.bottom_up_narrative && (
                        <div className="mt-10 border-t border-weave-ink pt-8">
                            <p className="editorial-label text-weave-teal mb-3">
                                The bottom-up build
                            </p>
                            <p className="text-weave-ink leading-relaxed text-lg max-w-3xl">
                                {c.bottom_up_narrative}
                            </p>
                        </div>
                    )}
                </section>

                {/* MVP */}
                {c.mvp && (
                    <section data-testid="mvp-section">
                        <div className="grid md:grid-cols-12 gap-10">
                            <div className="md:col-span-4">
                                <p className="editorial-label text-weave-teal">
                                    04 — MVP & Testing
                                </p>
                                <h2 className="text-3xl md:text-4xl font-light tracking-tight text-weave-ink mt-3">
                                    {c.mvp.concept_name}
                                </h2>
                            </div>
                            <div className="md:col-span-8 space-y-6">
                                <p className="text-lg text-weave-ink leading-relaxed">
                                    {c.mvp.description}
                                </p>

                                {c.mvp.key_features?.length > 0 && (
                                    <div>
                                        <p className="editorial-label text-weave-slate mb-3">
                                            Key features
                                        </p>
                                        <ul className="space-y-2">
                                            {c.mvp.key_features.map((f, i) => (
                                                <li
                                                    key={i}
                                                    className="flex gap-3 items-start text-weave-slate"
                                                >
                                                    <CheckCircle2 className="w-4 h-4 text-weave-teal mt-1 flex-shrink-0" />
                                                    <span>{f}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                <div className="border border-weave-border p-6">
                                    <p className="editorial-label text-weave-teal mb-3">
                                        Testing strategy
                                    </p>
                                    <p className="text-weave-ink leading-relaxed">
                                        {c.mvp.testing_strategy}
                                    </p>
                                </div>

                                {c.mvp.success_metrics?.length > 0 && (
                                    <div>
                                        <p className="editorial-label text-weave-slate mb-3">
                                            Success metrics
                                        </p>
                                        <div className="grid md:grid-cols-2 gap-3">
                                            {c.mvp.success_metrics.map((m, i) => (
                                                <div
                                                    key={i}
                                                    className="border border-weave-border p-4 text-sm text-weave-slate"
                                                >
                                                    {m}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {c.mvp.first_90_days && (
                                    <div className="border-l-4 border-weave-yellow bg-[#fff8e7] p-6">
                                        <p className="editorial-label text-[#a17320] mb-3">
                                            The first 90 days
                                        </p>
                                        <p className="text-weave-ink leading-relaxed">
                                            {c.mvp.first_90_days}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </section>
                )}

                {/* NEXT STEPS + CTA */}
                {c.next_steps?.length > 0 && (
                    <section
                        className="border-t border-weave-ink pt-10"
                        data-testid="next-steps-section"
                    >
                        <p className="editorial-label text-weave-teal">
                            05 — Next steps
                        </p>
                        <h2 className="text-3xl md:text-4xl font-light tracking-tight text-weave-ink mt-3">
                            What to do this week
                        </h2>
                        <ol className="mt-8 space-y-4 max-w-3xl">
                            {c.next_steps.map((step, i) => (
                                <li
                                    key={i}
                                    className="flex gap-5 items-start border-b border-weave-border pb-4"
                                >
                                    <span className="text-3xl font-light text-weave-teal w-10 tabular-nums">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <span className="text-weave-ink leading-relaxed pt-2">
                                        {step}
                                    </span>
                                </li>
                            ))}
                        </ol>
                    </section>
                )}

                {/* THREE CLARITIES — career flow only */}
                {isCareer && (pc || bc || ec) && (
                    <section
                        className="border-t border-weave-ink pt-12 space-y-14"
                        data-testid="three-clarities-section"
                    >
                        {pc && (
                            <div className="grid md:grid-cols-12 gap-10" data-testid="personal-clarity">
                                <div className="md:col-span-4">
                                    <p className="editorial-label text-weave-teal">01 — Personal Clarity</p>
                                    <h2 className="text-3xl md:text-4xl font-light tracking-tight text-weave-ink mt-3">Coming home to yourself</h2>
                                    <p className="text-sm text-weave-slate mt-3 italic">Sophrology-rooted · trajectory · values · resilience</p>
                                </div>
                                <div className="md:col-span-8 space-y-5">
                                    {pc.trajectory && (<div><p className="editorial-label text-weave-slate mb-2">Trajectory</p><p className="text-weave-ink leading-relaxed">{pc.trajectory}</p></div>)}
                                    {pc.values_and_beliefs && (<div><p className="editorial-label text-weave-slate mb-2">Values & beliefs</p><p className="text-weave-ink leading-relaxed">{pc.values_and_beliefs}</p></div>)}
                                    {pc.emotional_capacity && (<div><p className="editorial-label text-weave-slate mb-2">Emotional capacity</p><p className="text-weave-ink leading-relaxed">{pc.emotional_capacity}</p></div>)}
                                    {pc.resilience_note && (<div className="border-l-4 border-weave-yellow pl-5 py-2 bg-[#fff8e7]"><p className="text-weave-ink italic leading-relaxed">{pc.resilience_note}</p></div>)}
                                </div>
                            </div>
                        )}
                        {bc && (
                            <div className="grid md:grid-cols-12 gap-10" data-testid="business-clarity">
                                <div className="md:col-span-4">
                                    <p className="editorial-label text-weave-teal">02 — Business Clarity</p>
                                    <h2 className="text-3xl md:text-4xl font-light tracking-tight text-weave-ink mt-3">The quiet lifeboat</h2>
                                    <p className="text-sm text-weave-slate mt-3 italic">Model · fit · build-measure-learn · frugality</p>
                                </div>
                                <div className="md:col-span-8 space-y-5">
                                    {bc.the_quiet_lifeboat && (<div><p className="editorial-label text-weave-slate mb-2">Your quiet lifeboat</p><p className="text-weave-ink leading-relaxed">{bc.the_quiet_lifeboat}</p></div>)}
                                    {bc.model_shape && (<div><p className="editorial-label text-weave-slate mb-2">The shape of the model</p><p className="text-weave-ink leading-relaxed">{bc.model_shape}</p></div>)}
                                    {bc.smallest_mortal_bet && (<div className="border-l-4 border-weave-teal pl-5 py-2 bg-weave-surface-alt"><p className="editorial-label text-weave-teal mb-2">The smallest mortal bet</p><p className="text-weave-ink leading-relaxed">{bc.smallest_mortal_bet}</p></div>)}
                                </div>
                            </div>
                        )}
                        {ec && (
                            <div className="grid md:grid-cols-12 gap-10" data-testid="external-clarity">
                                <div className="md:col-span-4">
                                    <p className="editorial-label text-weave-teal">03 — External Clarity</p>
                                    <h2 className="text-3xl md:text-4xl font-light tracking-tight text-weave-ink mt-3">The language lab</h2>
                                    <p className="text-sm text-weave-slate mt-3 italic">Narrative arc · brand identity · emotional impact</p>
                                </div>
                                <div className="md:col-span-8 space-y-5">
                                    {ec.who_it_is_for && (<div><p className="editorial-label text-weave-slate mb-2">Who this is for</p><p className="text-weave-ink leading-relaxed">{ec.who_it_is_for}</p></div>)}
                                    {ec.the_language && (<div><p className="editorial-label text-weave-slate mb-2">The words that would make them say yes</p><p className="text-weave-ink leading-relaxed">{ec.the_language}</p></div>)}
                                    {ec.first_ten_conversations && (<div className="border-l-4 border-weave-red pl-5 py-2 bg-[#fdecea]"><p className="editorial-label text-weave-red mb-2">First ten conversations</p><p className="text-weave-ink leading-relaxed">{ec.first_ten_conversations}</p></div>)}
                                </div>
                            </div>
                        )}
                    </section>
                )}

                {/* TRIAL OFFER — guests only (members are already in the room) */}
                {!isMember && !isCareer && (
                <section
                    className="border-t border-weave-ink pt-12"
                    data-testid="trial-offer-section"
                >
                    <div className="bg-weave-surface-alt border border-weave-ink p-8 md:p-12">
                        <div className="flex items-center gap-2 mb-4">
                            <Sparkles className="w-4 h-4 text-weave-red" />
                            <p className="editorial-label text-weave-red">
                                Founder offer · 1% Club
                            </p>
                        </div>
                        <h2 className="text-3xl md:text-4xl font-light tracking-tight text-weave-ink leading-tight max-w-3xl">
                            Don't test this alone. Get a free month inside
                            the&nbsp;1%&nbsp;Club.
                        </h2>
                        <p className="mt-6 text-lg text-weave-slate leading-relaxed max-w-2xl">
                            A whole month, on us. Bring this MVP concept to
                            the room, get it reviewed, get warm intros, and
                            we'll help you promote it once it's working. If
                            it isn't for you, cancel before the month ends —
                            no charge.
                        </p>

                        {!trial && (
                            <div className="mt-8 flex flex-wrap items-center gap-4">
                                <button
                                    onClick={() => {
                                        setClaimEmail(
                                            report?.email_captured || ""
                                        );
                                        setClaimDialogOpen(true);
                                    }}
                                    className="btn-primary"
                                    data-testid="claim-trial-btn"
                                >
                                    <Sparkles className="w-4 h-4" />
                                    Claim my free month
                                </button>
                                <span className="text-xs text-weave-muted uppercase tracking-editorial">
                                    14 days to redeem · One per founder
                                </span>
                            </div>
                        )}

                        {trial && (
                            <div
                                className="mt-8 bg-weave-surface border border-weave-ink p-6 md:p-8"
                                data-testid="trial-code-card"
                            >
                                <div className="grid md:grid-cols-12 gap-6 items-center">
                                    <div className="md:col-span-7">
                                        <p className="editorial-label text-weave-slate mb-3">
                                            Your code
                                        </p>
                                        <div className="flex items-center gap-3 flex-wrap">
                                            <p
                                                className="text-3xl md:text-4xl font-medium tracking-wider text-weave-ink font-mono"
                                                data-testid="trial-code-value"
                                            >
                                                {trial.code}
                                            </p>
                                            <button
                                                onClick={copyCode}
                                                className="border border-weave-border hover:border-weave-ink px-3 py-2 text-xs uppercase tracking-editorial inline-flex items-center gap-2 transition-colors"
                                                data-testid="copy-code-btn"
                                            >
                                                {copiedCode ? (
                                                    <>
                                                        <Check className="w-3.5 h-3.5 text-weave-teal" />
                                                        Copied
                                                    </>
                                                ) : (
                                                    <>
                                                        <Copy className="w-3.5 h-3.5" />
                                                        Copy
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                        {trialExpiresHuman && (
                                            <p className="text-sm text-weave-muted mt-3">
                                                Expires{" "}
                                                <span className="text-weave-slate">
                                                    {trialExpiresHuman}
                                                </span>
                                                {trial.status &&
                                                    trial.status !== "offered" && (
                                                        <>
                                                            {" "}· status:{" "}
                                                            <span className="text-weave-teal capitalize">
                                                                {trial.status}
                                                            </span>
                                                        </>
                                                    )}
                                            </p>
                                        )}
                                    </div>
                                    <div className="md:col-span-5 md:text-right">
                                        <a
                                            href={
                                                trial.join_url ||
                                                "https://awake.wearetheweave.co.uk/join"
                                            }
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="btn-primary w-full md:w-auto justify-center"
                                            data-testid="trial-redeem-btn"
                                        >
                                            Open the 1% Club
                                            <ArrowRight className="w-4 h-4" />
                                        </a>
                                    </div>
                                </div>
                                <div className="hairline my-6" />
                                <ol className="text-sm text-weave-slate space-y-2 list-decimal pl-5">
                                    <li>
                                        Open the 1% Club join page (button
                                        above).
                                    </li>
                                    <li>Choose the monthly plan.</li>
                                    <li>
                                        At Stripe checkout, paste your code
                                        into the{" "}
                                        <span className="text-weave-ink">
                                            "Add promotion code"
                                        </span>{" "}
                                        field.
                                    </li>
                                </ol>
                            </div>
                        )}
                    </div>
                </section>
                )}

                {/* ACADEMY LADDER — career flow only */}
                {isCareer && !isMember && (
                    <section
                        className="border-t border-weave-ink pt-12"
                        data-testid="academy-ladder-section"
                    >
                        <div className="bg-weave-surface-alt border border-weave-ink p-8 md:p-12">
                            <div className="flex items-center gap-2 mb-4">
                                <Sparkles className="w-4 h-4 text-weave-red" />
                                <p className="editorial-label text-weave-red">The Weave Founder Academy</p>
                            </div>
                            <h2 className="text-3xl md:text-4xl font-light tracking-tight text-weave-ink leading-tight max-w-3xl">
                                You don't have to walk this next step alone.
                            </h2>
                            <p className="mt-6 text-lg text-weave-slate leading-relaxed max-w-2xl">
                                A year-long programme built around the three clarities you've just glimpsed. Personal Clarity is a 10-week Sophrology-rooted foundation you can join on its own, or step straight into the full Academy year and complete Business and External Clarity alongside a cohort of peers.
                            </p>

                            <div className="mt-8 grid md:grid-cols-3 gap-4">
                                <button
                                    type="button"
                                    onClick={openPlanPicker}
                                    className="text-left block p-6 border border-weave-ink bg-weave-ink text-weave-bg hover:bg-weave-teal-deep transition-colors w-full"
                                    data-testid="academy-apply-cta"
                                >
                                    <p className="editorial-label text-weave-yellow mb-3">Rung 01 — Full commitment</p>
                                    <p className="text-lg font-medium">Apply to the Academy</p>
                                    <p className="text-sm text-weave-bg/70 mt-2">Pick a plan and pay securely →</p>
                                </button>

                                <button
                                    type="button"
                                    onClick={() => { setAcademyEmail(report?.email_captured || claims?.e || ""); setAcademyDialogOpen(true); }}
                                    className="block text-left p-6 border border-weave-ink bg-weave-bg hover:bg-weave-surface-alt transition-colors"
                                    data-testid="academy-interest-cta"
                                >
                                    <p className="editorial-label text-weave-teal mb-3">Rung 02 — Nearly there</p>
                                    <p className="text-lg font-medium text-weave-ink">Register your interest</p>
                                    <p className="text-sm text-weave-slate mt-2">We'll hold a place open and reach out when the next cohort forms.</p>
                                    {academyRegistered && (
                                        <p className="mt-3 text-xs text-weave-teal inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Interest registered</p>
                                    )}
                                </button>

                                <button
                                    type="button"
                                    onClick={() => { setClaimEmail(report?.email_captured || claims?.e || ""); setClaimDialogOpen(true); }}
                                    className="block text-left p-6 border border-weave-border bg-weave-bg hover:border-weave-ink transition-colors"
                                    data-testid="academy-club-cta"
                                >
                                    <p className="editorial-label text-weave-muted mb-3">Rung 03 — Not ready yet?</p>
                                    <p className="text-lg font-medium text-weave-ink">Join the 1% Club</p>
                                    <p className="text-sm text-weave-slate mt-2">Build your confidence first. Claim a free month, cancel anytime.</p>
                                </button>
                            </div>

                            <div className="mt-8 border-l-4 border-weave-yellow pl-5 py-2 bg-[#fff8e7]">
                                <p className="text-sm text-weave-ink leading-relaxed">
                                    <span className="editorial-label text-[#a17320] mr-2">Note:</span>
                                    Free lifetime access to the 1% Club is included when you complete the <em>full</em> Academy year — not the Personal Clarity module on its own.
                                </p>
                            </div>

                            {trial && (
                                <div
                                    className="mt-8 bg-weave-surface border border-weave-ink p-6 md:p-8"
                                    data-testid="career-trial-code-card"
                                >
                                    <p className="editorial-label text-weave-slate mb-3">
                                        Your 1% Club code
                                    </p>
                                    <div className="flex items-center gap-3 flex-wrap">
                                        <p
                                            className="text-2xl md:text-3xl font-medium tracking-wider text-weave-ink font-mono"
                                            data-testid="career-trial-code-value"
                                        >
                                            {trial.code}
                                        </p>
                                        <button
                                            onClick={copyCode}
                                            className="border border-weave-border hover:border-weave-ink px-3 py-2 text-xs uppercase tracking-editorial inline-flex items-center gap-2 transition-colors"
                                            data-testid="career-copy-code-btn"
                                        >
                                            {copiedCode ? (
                                                <>
                                                    <Check className="w-3.5 h-3.5 text-weave-teal" />
                                                    Copied
                                                </>
                                            ) : (
                                                <>
                                                    <Copy className="w-3.5 h-3.5" />
                                                    Copy
                                                </>
                                            )}
                                        </button>
                                        <a
                                            href={trial.join_url || "https://awake.wearetheweave.co.uk/join"}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="btn-primary"
                                            data-testid="career-trial-redeem-btn"
                                        >
                                            <Sparkles className="w-4 h-4" />
                                            Redeem at awake
                                            <ExternalLink className="w-3.5 h-3.5" />
                                        </a>
                                    </div>
                                    {trialExpiresHuman && (
                                        <p className="text-sm text-weave-muted mt-3">
                                            Expires <span className="text-weave-slate">{trialExpiresHuman}</span>
                                            {trial.status && trial.status !== "offered" && (
                                                <> · status: <span className="text-weave-teal capitalize">{trial.status}</span></>
                                            )}
                                        </p>
                                    )}
                                    <p className="text-xs text-weave-muted mt-3">
                                        We've also emailed this to you. Single-use · 14 days to redeem.
                                    </p>
                                    <label
                                        className="mt-5 flex items-start gap-3 cursor-pointer group"
                                        data-testid="career-academy-waitlist-label"
                                    >
                                        <input
                                            type="checkbox"
                                            onChange={addToAcademyWaitlist}
                                            checked={waitlistedForAcademy}
                                            disabled={waitlistingForAcademy || waitlistedForAcademy}
                                            className="mt-1 w-4 h-4 accent-weave-teal border border-weave-ink rounded-none shrink-0 cursor-pointer disabled:cursor-default"
                                            data-testid="career-academy-waitlist-checkbox"
                                        />
                                        <span className="text-sm text-weave-ink leading-snug group-hover:text-weave-teal transition-colors">
                                            {waitlistedForAcademy ? (
                                                <span className="text-weave-teal inline-flex items-center gap-1.5">
                                                    <CheckCircle2 className="w-4 h-4" />
                                                    You're on the Academy waiting list — we'll be in touch when the next cohort opens.
                                                </span>
                                            ) : (
                                                <>
                                                    Also add me to <span className="font-medium">The Weave Academy</span> waiting list — we'll email when the next cohort opens. No obligation.
                                                </>
                                            )}
                                        </span>
                                    </label>
                                </div>
                            )}
                        </div>
                    </section>
                )}

                {/* CLOSING CTA — hidden on career reports (Academy ladder is the closer) */}
                {isCareer ? null : isMember ? (
                <section
                    className="bg-weave-ink text-weave-bg p-10 md:p-14"
                    data-testid="closing-cta-member"
                >
                    <p className="editorial-label text-weave-yellow mb-4">
                        Bring it to the room
                    </p>
                    <h2 className="text-3xl md:text-4xl font-light tracking-tight">
                        You don't have to test this alone.
                    </h2>
                    <p className="mt-4 text-weave-bg/70 max-w-2xl">
                        Pin this opportunity to your dashboard and bring the
                        MVP concept to your next 1% Club session — we'll help
                        you validate it, get warm intros, and promote it once
                        it's working.
                    </p>
                    <div className="mt-8 flex flex-wrap gap-4">
                        <a
                            href={AWAKE_URLS.dashboard}
                            className="inline-flex items-center gap-2 bg-weave-bg text-weave-ink px-7 py-3 text-sm font-medium hover:bg-weave-yellow transition-colors"
                            data-testid="closing-dashboard-cta"
                        >
                            Back to your dashboard
                            <ArrowRight className="w-4 h-4" />
                        </a>
                        <button
                            onClick={handleDownloadClick}
                            className="inline-flex items-center gap-2 border border-weave-bg/40 text-weave-bg px-7 py-3 text-sm font-medium hover:bg-weave-bg/10 transition-colors"
                            data-testid="closing-download-btn"
                        >
                            <Download className="w-4 h-4" />
                            Download the PDF
                        </button>
                    </div>
                </section>
                ) : (
                <section className="bg-weave-ink text-weave-bg p-10 md:p-14">
                    <p className="editorial-label text-weave-yellow mb-4">
                        Innovation through connection
                    </p>
                    <h2 className="text-3xl md:text-4xl font-light tracking-tight">
                        Ready to test it? Don't go alone.
                    </h2>
                    <p className="mt-4 text-weave-bg/70 max-w-2xl">
                        Bring this MVP concept to the 1% Club. We'll help you
                        validate it, get it in front of the right people, and
                        promote it once it's working.
                    </p>
                    <div className="mt-8 flex flex-wrap gap-4">
                        <a
                            href="https://awake.wearetheweave.co.uk/join"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 bg-weave-bg text-weave-ink px-7 py-3 text-sm font-medium hover:bg-weave-yellow transition-colors"
                            data-testid="closing-club-join"
                        >
                            Join the 1% Club
                            <ArrowRight className="w-4 h-4" />
                        </a>
                        <button
                            onClick={handleDownloadClick}
                            className="inline-flex items-center gap-2 border border-weave-bg/40 text-weave-bg px-7 py-3 text-sm font-medium hover:bg-weave-bg/10 transition-colors"
                            data-testid="closing-download-btn"
                        >
                            <Download className="w-4 h-4" />
                            Download the PDF
                        </button>
                    </div>
                </section>
                )}
            </main>

            <Footer />

            <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
                <DialogContent
                    className="sm:max-w-md bg-weave-bg border-weave-ink"
                    data-testid="email-gate-dialog"
                >
                    <DialogHeader>
                        <p className="editorial-label text-weave-teal mb-2">
                            One small thing
                        </p>
                        <DialogTitle className="text-2xl font-light tracking-tight text-weave-ink">
                            Where shall we send your report?
                        </DialogTitle>
                        <DialogDescription className="text-weave-slate pt-2">
                            We'll email you the PDF and download it now. No
                            spam — promise.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleUnlock} className="space-y-5 mt-4">
                        <div>
                            <label className="editorial-label text-weave-slate">
                                Email
                            </label>
                            <Input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@yourcompany.com"
                                required
                                className="mt-2 bg-weave-surface focus-visible:ring-weave-teal"
                                data-testid="email-gate-input"
                                autoFocus
                            />
                        </div>
                        <label className="flex items-center gap-3 text-sm text-weave-slate cursor-pointer">
                            <Checkbox
                                checked={isClubMember}
                                onCheckedChange={(v) => setIsClubMember(!!v)}
                                data-testid="club-member-checkbox"
                            />
                            <span>I'm already a 1% Club member</span>
                        </label>
                        <button
                            type="submit"
                            disabled={unlocking}
                            className="btn-primary w-full disabled:opacity-50"
                            data-testid="email-gate-submit"
                        >
                            {unlocking ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Sending…
                                </>
                            ) : (
                                <>
                                    <Mail className="w-4 h-4" />
                                    Send me the PDF
                                </>
                            )}
                        </button>
                        <p className="text-xs text-weave-muted text-center">
                            By submitting you agree to receive your report.
                        </p>
                    </form>
                </DialogContent>
            </Dialog>

            <Dialog open={claimDialogOpen} onOpenChange={setClaimDialogOpen}>
                <DialogContent
                    className="sm:max-w-md bg-weave-bg border-weave-ink"
                    data-testid="claim-trial-dialog"
                >
                    <DialogHeader>
                        <p className="editorial-label text-weave-red mb-2">
                            1% Club · Free month
                        </p>
                        <DialogTitle className="text-2xl font-light tracking-tight text-weave-ink">
                            Where shall we send your code?
                        </DialogTitle>
                        <DialogDescription className="text-weave-slate pt-2">
                            We'll email it and show it on this page. One trial
                            per founder.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={claimTrial} className="space-y-5 mt-4">
                        <div>
                            <label className="editorial-label text-weave-slate">
                                Email
                            </label>
                            <Input
                                type="email"
                                value={claimEmail}
                                onChange={(e) => setClaimEmail(e.target.value)}
                                placeholder="you@yourcompany.com"
                                required
                                className="mt-2 bg-weave-surface focus-visible:ring-weave-teal"
                                data-testid="claim-trial-email"
                                autoFocus
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={claiming}
                            className="btn-primary w-full disabled:opacity-50"
                            data-testid="claim-trial-submit"
                        >
                            {claiming ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Minting your code…
                                </>
                            ) : (
                                <>
                                    <Sparkles className="w-4 h-4" />
                                    Send me my code
                                </>
                            )}
                        </button>
                        <p className="text-xs text-weave-muted text-center">
                            14 days to redeem · single use · cancel any time
                        </p>
                    </form>
                </DialogContent>
            </Dialog>

            {/* ACADEMY PLAN PICKER */}
            <AcademyPlanDialog
                open={planDialogOpen}
                onOpenChange={setPlanDialogOpen}
                plans={academyPlans}
                checkoutLoading={checkoutLoading}
                onSelect={startAcademyCheckout}
                onRegisterInterest={() => {
                    setPlanDialogOpen(false);
                    setAcademyEmail(report?.email_captured || claims?.e || "");
                    setAcademyDialogOpen(true);
                }}
            />

            {/* ACADEMY INTEREST DIALOG */}
            <AcademyInterestDialog
                open={academyDialogOpen}
                onOpenChange={setAcademyDialogOpen}
                email={academyEmail}
                onEmailChange={setAcademyEmail}
                interestType={academyType}
                onInterestTypeChange={setAcademyType}
                note={academyNote}
                onNoteChange={setAcademyNote}
                onSubmit={registerAcademyInterest}
                submitting={academyRegistering}
                submitted={academyRegistered}
            />

        </div>
    );
}
